import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import cn from 'clsx'

// LuxKit Components
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'LuxKit/Typography/Heading'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import TextButton from 'LuxKit/Button/TextButton'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import SolidExclamationCircleIcon from 'LuxKit/Icons/solid/SolidExclamationCircleIcon'
import { CalendarItemBundleData } from '../hooks/types'

// Utils
import { getInventoryMessage } from '../hooks/utils'

// Types
interface TripOption {
  id: string
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  price: number
  memberPrice?: number
  currency: string
  seatsLeft?: number
  nights: number
  available: boolean
}

interface Props {
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  nights: number
  priceMode: 'total' | 'perPerson'
  luxPlusEnabled: boolean
  isSelected: boolean
  isVisible: boolean
  bundleData: CalendarItemBundleData
  onSelect: (option: TripOption) => void
}

// Styled Components
const TripOptionRow = styled.div`
  display: none;
  grid-template-columns: 9fr 7fr 7fr;
  align-items: center;
  padding: ${rem(20)} 0;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};

  &.isVisible {
    display: grid;
  }
`

const InventoryGroup = styled(Group)`
  width: ${rem(170)};
`

const SelectButton = styled(TextButton)`
  width: ${rem(80)};
`

const DateRangeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
`

const SmallSup = styled.sup`
  font-size: 0.6em;
  vertical-align: super;
`

function MaldivesOfferCalendarItem(props: Props) {
  const {
    departDate,
    returnDate,
    checkIn,
    checkOut,
    nights,
    priceMode,
    luxPlusEnabled,
    isSelected,
    isVisible,
    onSelect,
    bundleData,
  } = props

  // Select price based on priceMode
  const regularPrice = priceMode === 'perPerson' ? bundleData.pricePerPerson : bundleData.totalPrice
  const memberPrice = priceMode === 'perPerson' ? bundleData.memberPricePerPerson : bundleData.memberTotalPrice

  const isMemberPrice = luxPlusEnabled && !!memberPrice
  const displayPrice = isMemberPrice && memberPrice ? memberPrice : regularPrice

  const option: TripOption = {
    id: `${checkIn}-${nights}n`,
    departDate: departDate,
    returnDate: returnDate,
    checkIn: checkIn,
    checkOut: checkOut,
    nights,
    price: regularPrice,
    memberPrice: memberPrice,
    currency: 'AUD',
    seatsLeft: bundleData.seatsLeft,
    available: Boolean(bundleData.available),
  }

  // Render select button based on state
  const selectButton = (isSelected && bundleData.available) ? (
    <SelectButton
      kind="primary"
      nonInteractable
      tabIndex={-1}
    >
      Selected
    </SelectButton>
  ) : (
    <SelectButton
      kind="secondary"
      onClick={() => onSelect(option)}
      disabled={!bundleData.available || bundleData.hasNoPackage}
    >
      {bundleData.available ? 'Select' : 'Unavailable'}
    </SelectButton>
  )

  // Render price display based on availability
  let priceDisplay
  if (bundleData.hasNoPackage) {
    priceDisplay = (
      <BodyText variant="small" colour="neutral-three">
        No package available
      </BodyText>
    )
  } else if (bundleData.fetching) {
    priceDisplay = (
      <BodyText variant="small" colour="neutral-three">
        Loading...
      </BodyText>
    )
  } else if (displayPrice > 0) {
    priceDisplay = (
      <>
        <PriceRowPriceCaption>From</PriceRowPriceCaption>
        <div>
          <Heading as="span" variant="heading4" colour={isMemberPrice ? 'lux-plus' : undefined} wrap="no-wrap">
            <FormatCurrency value={displayPrice} format="roundedDollar" />
          </Heading>
          {' '}
          <Caption as="span" variant="large" colour={isMemberPrice ? 'lux-plus' : 'neutral-two'} weight="normal" format="lowercase">
            {priceMode === 'perPerson' ? '/person' : 'total'}
          </Caption>
        </div>
      </>
    )
  } else {
    priceDisplay = (
      <BodyText variant="medium" colour="neutral-three">
        Unavailable
      </BodyText>
    )
  }

  return (
    <TripOptionRow className={cn({ isVisible })}>
      {/* Column 1: Depart Date */}
      <DateRangeContainer>
        <div>
          <BodyText variant="small" colour="neutral-three">
            {moment(departDate).format('dddd')}
          </BodyText>
          <Heading variant="heading4" wrap="no-wrap">
            {moment(departDate).format('DD MMM YYYY')}
          </Heading>
        </div>
        <Group direction="vertical" gap={4} horizontalAlign="center">
          <LineArrowRightIcon size="M" />
          <BodyText variant="small" colour="neutral-three">
            {nights} nights + flights
          </BodyText>
        </Group>
        <div>
          <BodyText variant="small" colour="neutral-three">
            {moment(checkOut).format('dddd')}
          </BodyText>
          <Heading variant="heading4" wrap="no-wrap">
            {moment(checkOut).format('DD MMM YYYY')}<SmallSup>+1</SmallSup>
          </Heading>
        </div>
      </DateRangeContainer>

      {/* Column 3: Availability Warning */}
      {(() => {
        const inventoryMessage = getInventoryMessage(bundleData.seatsLeft, bundleData.roomsLeft)

        return (
          <Group direction="vertical" horizontalAlign="center" gap={0}>
            <InventoryGroup direction="horizontal" verticalAlign="center" gap={4}>
              {inventoryMessage !== '' && <SolidExclamationCircleIcon colour="urgency" size="XS" />}
              <BodyText variant="small" colour="critical">
                {inventoryMessage}
              </BodyText>
            </InventoryGroup>
          </Group>
        )
      })()}

      {/* Column 4: Price and Select Button */}
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={12}>
        <VerticalSpacer gap={4}>
          {priceDisplay}
        </VerticalSpacer>
        {selectButton}
      </Group>
    </TripOptionRow>
  )
}

export default MaldivesOfferCalendarItem
